import logger from '@eventbrite/client-logger';

import {
    shouldPerform3DS2Validation,
    threeDomainSecurePromise,
} from '../threeDomainSecure';

import { createNewVaultInstrument as createNewVaultInstrumentApi } from '../../api/users';

import { handleAnalyticsTokenOnError } from '../tokens';
import {
    CHECKOUT_FORM_NAME,
    PAYMENT_METHODS,
    PLACE_ORDER_TIMEOUT,
} from '../../constants';

import {
    isAllInfoRequiredToSavePaymentMethodAvailable,
    clearPaymentMethods as clearPaymentMethodsApi,
    formatCreditCardInstrument,
    placeOrder as placeOrderApi,
    placeOrderRetrying as placeOrderRetryingApi,
    savePaymentMethods as savePaymentMethodsApi,
    saveOrder as saveOrderApi,
} from '../../api/order';

import {
    getIsNosSourceOfTruth,
    getOrderId,
} from '../../selectors/order/typedOrderSelectors';
import {
    getAttendeeDeliveryMethodsPayloadForPlacingOrder,
    getEmailConfiguration,
    getTotalCostsFromGrossMajorValue,
    isPaidOrder as isOrderToBePaid,
} from '../../selectors/order';
import { getCurrency, getEventCountryCode } from '../../selectors/event';
import { getSurvey } from '../../selectors/survey';
import { getUserId, isUserLoggedIn } from '../../selectors/user';

import {
    handleUpdateOrder,
    handleUpdateOrderToken,
    clearOrderTimeout,
} from './common';
import {
    setOrderUpdatingAction,
    setHasRetryPaymentError,
    setRedirectedPaymentTimeoutIdAction,
    clearRedirectedPaymentTimeoutIdAction,
} from './actions';
import { hasSelectedRedirectedPaymentMethod } from '../../selectors/order/hasSelectedRedirectedPaymentMethod';
import { OrderActionType } from './types';
import { updateOrderAction } from './updateOrderAction';
import { clearPaymentMethods } from './paymentMethods';
import { getFeatureFlags } from '../../selectors/app';
import { GryllsAction, triggerTrackingPixelAction } from '../pixelAnalytics';

import {
    getPostMessageActionParamsFromState,
    handleOrderError,
    userIsStoringNewCreditCard,
    checkIfUserWasLoggedIn,
} from './utils';
import { updateBackgroundTimer } from '../timer';

import {
    getSaveOrderFormData,
    getSavePaymentMethodsFormData,
    getPlaceOrderFormData,
} from './formData';

import { checkTaxExemptionApplies } from '../taxExemptions';
import { logEvent } from '@eventbrite/statsig';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { handleOrderErrorInTicketSelectionPage } from '../../actions/order/utils';
import { isTicketSelectionFullSizePage } from '../../selectors/routing';
import { track } from '@eventbrite/datalayer-library';
import { HeapEventName } from '../../constants/heap';

// TODO: EB-17141 This is exported only for tests, we should not export it
/**
 * @deprecated TODO: EB-17141 This is exported only for tests, we should not export it
 */
export const placeOrderPromise = ({
    dispatch,
    getState,
    paymentData = {},
    countryCode,
}) => {
    const state = getState();
    const body = getEmailConfiguration(state);
    const orderId = getOrderId(state);
    const currency = getCurrency(state);
    const isNOS = getIsNosSourceOfTruth(state);

    // TODO: EB-130249 Don't use placeOrderRetryingApi after race condition is fixed
    const placeOrderApiPromise = getFeatureFlags(state)
        .launchEmbeddedCheckoutRetryOnPlaceError
        ? placeOrderRetryingApi(orderId, body, {
              currency,
              paymentMethod: paymentData.paymentMethod,
          })
        : placeOrderApi(orderId, body);

    return placeOrderApiPromise
        .then((placeOrderResponse) => {
            const state = getState();

            clearOrderTimeout(getState, dispatch);

            if (placeOrderResponse.response) {
                handleUpdateOrder(dispatch, placeOrderResponse.response);
                handleUpdateOrderToken(dispatch, placeOrderResponse.headers);
            }

            dispatch({
                type: OrderActionType.updateOrder,
                payload: {
                    isPlaced: true,
                },
            });

            // Check if a new user was created
            if (!isUserLoggedIn(state)) {
                checkIfUserWasLoggedIn()(dispatch, getState);
            }

            // Store the user's credit card to their "wallet" if they selected to save it.
            if (userIsStoringNewCreditCard(paymentData) && !isNOS) {
                const userId = getUserId(state);
                const currency = getCurrency(state);

                const { instrument, billingInformation } =
                    formatCreditCardInstrument({
                        countryCode,
                        payment: paymentData.payment,
                        currency,
                        instrumentType: PAYMENT_METHODS.CREDIT,
                    });

                createNewVaultInstrumentApi(
                    userId,
                    instrument,
                    billingInformation,
                );
            }

            return placeOrderResponse;
        })
        .catch((error) => {
            handleAnalyticsTokenOnError(dispatch, getState);

            if (isTicketSelectionFullSizePage(getState())) {
                handleOrderErrorInTicketSelectionPage(dispatch);
            }

            handleOrderError(dispatch, error);
            dispatch(updateBackgroundTimer());
        });
};

// TODO: EB-17141 This is exported only for tests, we should not export it
/**
 * @deprecated TODO: EB-17141 This is exported only for tests, we should not export it
 */
export const savePaymentMethods = async ({
    dispatch,
    getState,
    formData,
    clearPaymentMethodsWhenMissingInfo = false,
}) => {
    const state = getState();
    const isPaidOrder = isOrderToBePaid(state);
    const isNOS = getIsNosSourceOfTruth(state);

    if (!isPaidOrder) return;

    const orderId = getOrderId(state);
    const currency = getCurrency(state);
    const countryCode = getEventCountryCode(state);
    const paymentMethodsFormData = getSavePaymentMethodsFormData({
        formData,
        state,
    });

    const hasAllInfoRequiredToSavePaymentMethods =
        isAllInfoRequiredToSavePaymentMethodAvailable({
            ...paymentMethodsFormData,
            countryCode,
            currency,
        });

    const shouldClearPaymentMethods =
        clearPaymentMethodsWhenMissingInfo &&
        !hasAllInfoRequiredToSavePaymentMethods;

    let responsePromise;

    if (shouldClearPaymentMethods) {
        responsePromise = clearPaymentMethodsApi(orderId);
    } else {
        // EB-163838 Workaround to update the fees of Paypal payment method. The save petition
        // is returning 400 (nonce attribute missing), so we need to do a clear first.
        const isNonceBraintree =
            paymentMethodsFormData.paymentMethod ===
            PAYMENT_METHODS.NONCE_BRAINTREE;

        if (isNonceBraintree) {
            await dispatch(clearPaymentMethods());
        }

        responsePromise = savePaymentMethodsApi({
            orderId,
            countryCode,
            paymentData: paymentMethodsFormData,
            currency,
            isNOS,
        });
    }

    try {
        const response = await responsePromise;
        const updatedOrder = response.response;

        // In GST taxes we don't get `updatedOrder.costs.tax` back when the order is exempted.
        // We keep the previous value in `state.order.tax.taxBeforeExemption` in order to show the GST tax as exempted in the order summary.
        // We set `updatedOrder.costs.tax.taxBeforeExemption` so it overwrites `state.order.costs.tax.taxBeforeExemption` when we dispatch `updateOrder` action.
        // If we always set `updatedOrder.order.costs.tax.taxBeforeExemption` regardless `updatedOrder.costs.tax` then we will always be in the "order is exempted" status.
        // That's why we only take `taxBeforeExemption` from state when  there are taxes coming back from Order Service
        if (
            state.order &&
            state.order.costs &&
            state.order.costs.tax &&
            updatedOrder.costs &&
            updatedOrder.costs.tax
        ) {
            updatedOrder.costs.tax.taxBeforeExemption =
                state.order.costs.tax.taxBeforeExemption;
        }
        dispatch(updateOrderAction(updatedOrder));

        return response;
    } finally {
        await dispatch(checkTaxExemptionApplies());
    }
};

// Promise in charge of saving the payment method to the order
const saveOrder = async ({ dispatch, getState, formData }) => {
    const state = getState();
    const orderId = getOrderId(state);
    const deliveryMethods =
        getAttendeeDeliveryMethodsPayloadForPlacingOrder(state);

    const {
        prePaymentSurveySettings: {
            getStatistics,
            getAnswersPayloadForBackend,
        },
        preOrderConfirmationSurveySettings: {
            hasRequiredQuestions: isPostCheckoutQuestionsEnabled,
            hasQuestions: isOrderFormSplittedInTwoPages,
        },
    } = getSurvey(state);

    if (!isOrderFormSplittedInTwoPages) {
        const { statsigMetrics } = getStatistics(formData);

        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_STEP_SUCCESS_ORGANIZER_QUESTIONS_RATIO,
            undefined,
            {
                ...statsigMetrics,
                currentPage: CHECKOUT_FORM_NAME,
            },
        );
    }

    const saveOrderFormData = getSaveOrderFormData({ formData, state });
    const surveyAnswersPayload = getAnswersPayloadForBackend(saveOrderFormData);

    await saveOrderApi({
        orderId,
        surveyAnswersPayload,
        deliveryMethods,
        isPostCheckoutQuestionsEnabled,
    });

    dispatch(triggerTrackingPixelAction(GryllsAction.addPaymentInfo));

    await savePaymentMethods({ dispatch, getState, formData });
};

/**
 * @param {PaymentMethodType} newPaymentMethod
 * @param {PaymentMethodType} previousPaymentMethod
 */
export const updateOrderPaymentMethod =
    (newPaymentMethod, previousPaymentMethod) => async (dispatch, getState) => {
        if (newPaymentMethod === previousPaymentMethod) return;

        const state = getState();

        const formDataCleaned = getSurvey(
            state,
        ).prePaymentSurveySettings.getCleanedFormData(
            state.form.checkoutForm.values,
        );

        dispatch(setOrderUpdatingAction(true));

        try {
            await savePaymentMethods({
                dispatch,
                getState,
                formData: formDataCleaned,
                clearPaymentMethodsWhenMissingInfo: true,
            });
        } finally {
            dispatch(setOrderUpdatingAction(false));
        }
    };

/**
 * Saves and places the user's order. This function will choose the correct flow and state updates
 * based on the user's selected payment method and information they have entered.
 */
export const placeOrder =
    ({ formData }) =>
    (dispatch, getState) => {
        const state = getState();
        const orderId = getOrderId(state);
        const { eventId, widgetParentUrl } =
            getPostMessageActionParamsFromState(state);

        dispatch(setHasRetryPaymentError(false));

        const isRedirectedPayment = hasSelectedRedirectedPaymentMethod(state);

        if (isRedirectedPayment) {
            const redirectedPaymentTimeoutId = setTimeout(() => {
                track({
                    eventName: HeapEventName.externalPaymentPageNotOpened,
                    eventData: {
                        orderId,
                        timeoutDuration: PLACE_ORDER_TIMEOUT,
                    },
                });
            }, PLACE_ORDER_TIMEOUT);

            dispatch(
                setRedirectedPaymentTimeoutIdAction(redirectedPaymentTimeoutId),
            );
        }

        const saveAndPlacePromise = saveOrder({
            dispatch,
            getState,
            formData,
        })
            .then(async () => {
                await dispatch(checkTaxExemptionApplies());

                const countryCode = getEventCountryCode(state);
                const paymentData = getPlaceOrderFormData({ formData, state });

                if (shouldPerform3DS2Validation({ state, paymentData })) {
                    const currency = getCurrency(state);
                    const amount = getTotalCostsFromGrossMajorValue(state);
                    const userCountry = state.app.locale;

                    await threeDomainSecurePromise({
                        dispatch,
                        savePaymentMethods: savePaymentMethodsApi,
                        handleOrderError,
                        paymentData,
                        currency,
                        countryCode,
                        amount,
                        orderId,
                        eventId,
                        userCountry,
                    });
                }

                return placeOrderPromise({
                    dispatch,
                    getState,
                    paymentData,
                    countryCode,
                });
            })
            .catch((customSubmissionError) => {
                clearOrderTimeout(getState, dispatch);

                logger.error(customSubmissionError, null, {
                    event_id: eventId,
                    request: 'save_order',
                });
                throw customSubmissionError;
            });

        return {
            dispatch,
            eventId,
            orderId,
            widgetParentUrl,
            formData,
            saveAndPlacePromise,
        };
    };
